/* Replacement cubicle parts — phone flow (<=749px) and desktop order summary.
   Pairs with snippets/replacement-parts-builder.liquid, which stays the single pricing,
   sheet-optimisation and basket engine. Everything here goes through window.BW_PARTS_API:
   each component card is one cut-list row in the engine, each colour is one colour group.

   Phone steps: 1 What (door / partition / pilaster) · 2 Material & colour · 3 Sizes · 4 Hardware · 5 Review.
   A colour group is one material + colour, so the same colour in CGL and MFC is two groups.
   Desktop keeps the cutting-list workflow; this file only adds a sticky order summary there. */
(function () {
  'use strict';
  const app = document.querySelector('[data-bdp]');
  const pbRoot = document.querySelector('[data-lead-part]');
  if (!app || !pbRoot) return;
  let cfg = {};
  try { cfg = JSON.parse((document.querySelector('[data-bdp-config]') || {}).textContent || '{}'); } catch (e) { cfg = {}; }

  const PHONE = window.matchMedia('(max-width: 749px)');
  const money = p => '£' + (Number(p) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const plural = (n, w) => n + ' ' + w + (n === 1 ? '' : 's');
  const TYPE_ICON = {
    door: '<svg viewBox="0 0 60 90" aria-hidden="true"><rect x="12" y="6" width="36" height="78" rx="2"/><rect x="8" y="14" width="5" height="10" rx="1"/><rect x="8" y="66" width="5" height="10" rx="1"/><circle cx="41" cy="45" r="3"/></svg>',
    partition: '<svg viewBox="0 0 60 90" aria-hidden="true"><rect x="6" y="10" width="48" height="66" rx="2"/><rect x="9" y="16" width="5" height="8" rx="1"/><rect x="46" y="16" width="5" height="8" rx="1"/><rect x="46" y="62" width="5" height="8" rx="1"/><rect x="9" y="62" width="5" height="8" rx="1"/><rect x="44" y="76" width="4" height="10"/></svg>',
    pilaster: '<svg viewBox="0 0 60 90" aria-hidden="true"><rect x="24" y="4" width="14" height="76" rx="2"/><rect x="34" y="12" width="4" height="8" rx="1"/><rect x="34" y="40" width="4" height="8" rx="1"/><rect x="34" y="68" width="4" height="8" rx="1"/><rect x="29" y="80" width="4" height="8"/></svg>'
  };
  // Phone drawing areas: small, so the mm labels read at phone size.
  const FIT = { door: { w: 110, h: 190 }, partition: { w: 150, h: 150 }, pilaster: { w: 60, h: 190 } };
  const MEASURE = {
    door: 'Measure the door itself: its overall width and overall height, edge to edge. Missing door? Send us photos and the opening size and we will confirm the size before anything is cut.',
    partition: 'Width is the partition panel’s horizontal size, from the wall end to the pilaster end. Height is the panel itself, top edge to bottom edge (not floor to top).',
    pilaster: 'Width is the front face of the pilaster, edge to edge. Height is the panel itself, top edge to bottom edge.'
  };

  function start(A) {
    if (app.dataset.bdpMounted) return;
    app.dataset.bdpMounted = '1';
    const TYPES = A.types;
    const MATS = A.materials || { cgl: { key: 'cgl', label: '13mm compact grade', edgeNote: 'All edges machined, slight round' } };
    const MAT_NOTE = { cgl: 'Solid black core, waterproof, machined edges', mfc: 'Melamine-faced chipboard, ABS edged, for dry washrooms' };
    const matOf = k => MATS[k] || MATS.cgl;
    const COLOURS = A.swatches().map((c, i) => Object.assign({ key: 'c' + i }, c));
    const brandOf = c => { const m = (c.manu || '').toLowerCase(); return m.includes('fundermax') ? 'Fundermax' : m.includes('polyrey') ? 'Polyrey' : m.includes('pfleiderer') ? 'Pfleiderer' : (c.manu || 'Other'); };
    const BRANDS = Array.from(new Set(COLOURS.map(brandOf)));
    const colourKeyOf = col => { const c = COLOURS.find(x => x.name === col.name && x.code === col.code && x.manu === col.manu); return c ? c.key : ''; };
    const byKey = k => COLOURS.find(c => c.key === k) || null;

    /* ------------------------------ State ------------------------------ */
    const S = { step: 1, q: '', brand: 'All', all: false, ed: null, touched: {}, tried: false, review: false };
    function newEdit(type, colourKey) { return { tr: null, isNew: true, type: type || A.lead, mat: lastMaterial(), colour: colourKey || lastColour(), l: '', w: '', q: '1', name: '', hw: null }; }
    function lastColour() {
      const gs = A.groups().filter(g => g.colour && g.colour.name);
      return gs.length ? colourKeyOf(gs[gs.length - 1].colour) : '';
    }
    function lastMaterial() {
      const all = A.groups(), gs = all.filter(g => g.colour && g.colour.name);
      return (gs.length ? gs[gs.length - 1].material : (all[0] || {}).material) || 'cgl';
    }
    // Every component (cut-list row with a size) across colours, for the review list.
    function components() {
      const out = [];
      A.groups().forEach(g => {
        A.rows(g.id).forEach(tr => {
          const d = A.rowData(tr);
          if (!(d.l > 0 || d.w > 0)) return;
          const hw = A.rowHw(tr);
          // hwTodo: sized, but the customer hasn't said Yes or No to hardware yet.
          out.push({ tr, gid: g.id, colour: g.colour, d, hw, problem: A.rowProblem(g.id, d.type, d.l, d.w), hwTodo: !!A.hwAnswered && d.l > 0 && d.w > 0 && !A.hwAnswered(d.type, hw) });
        });
      });
      return out;
    }

    /* ------------------------------ Engine writes ------------------------------ */
    // The colour group for a material + colour: an existing one, the empty starter, or a new group.
    function groupForColour(key, mat) {
      const c = byKey(key);
      if (!c) return null;
      mat = mat || 'cgl';
      const gs = A.groups();
      const hit = gs.find(g => (g.material || 'cgl') === mat && g.colour && g.colour.name === c.name && g.colour.code === c.code && g.colour.manu === c.manu);
      if (hit) return hit.id;
      const empty = gs.find(g => !g.colour || !g.colour.name);
      const gid = empty ? empty.id : A.addGroup();
      if (A.setMaterial) A.setMaterial(gid, mat);
      A.setColour(gid, c);
      return gid;
    }
    function gidOfRow(tr) { const card = tr.closest('[data-step2-group-id]'); return card ? card.getAttribute('data-step2-group-id') : null; }
    // Make sure the edited component exists as a row in the right colour group.
    function ensureRow() {
      const ed = S.ed;
      if (!ed || !ed.colour) return null;
      const gid = groupForColour(ed.colour, ed.mat);
      if (!gid) return null;
      if (ed.tr && document.contains(ed.tr) && gidOfRow(ed.tr) === gid) {
        if (A.rowData(ed.tr).type !== ed.type) A.setRow(ed.tr, { type: ed.type });
        return ed.tr;
      }
      const old = ed.tr && document.contains(ed.tr) ? ed.tr : null;
      const hw = old ? A.rowHw(old) : ed.hw;
      const tr = A.addRow(gid, ed.type);
      A.setRow(tr, { type: ed.type, l: ed.l, w: ed.w, q: ed.q || '1', name: ed.name, hw: hw || undefined });
      if (old) { A.removeRow(old); cleanEmptyGroups(); }
      ed.tr = tr;
      return tr;
    }
    function cleanEmptyGroups() {
      const gs = A.groups();
      gs.slice(1).forEach(g => { if (!A.rows(g.id).some(tr => { const d = A.rowData(tr); return d.l > 0 || d.w > 0; })) A.removeGroup(g.id); });
    }
    function writeSizes() {
      const ed = S.ed;
      if (!ed || !ed.tr) return;
      A.setRow(ed.tr, { l: ed.l, w: ed.w, q: ed.q || '1', name: ed.name });
    }
    function discardNew() {
      const ed = S.ed;
      if (ed && ed.isNew && ed.tr && document.contains(ed.tr)) { A.removeRow(ed.tr); cleanEmptyGroups(); }
    }

    /* ------------------------------ Markup ------------------------------ */
    app.innerHTML = `
      <ol class="bdp-progress" aria-label="Steps">
        <li data-p="1"><span>1</span>Part</li><li data-p="2"><span>2</span>Colour</li><li data-p="3"><span>3</span>Size</li><li data-p="4"><span>4</span>Hardware</li><li data-p="5"><span>5</span>Review</li>
      </ol>
      <div class="bdp-body" data-bdp-body></div>
      <div class="bdp-bar" data-bdp-bar><div class="bdp-bar__price"><strong data-bdp-bar-price>—</strong><span data-bdp-bar-sub></span></div><button type="button" class="bdp-btn bdp-btn--primary" data-bdp-bar-btn>Continue</button></div>`;
    const body = app.querySelector('[data-bdp-body]');
    const fsDlg = document.createElement('dialog');
    fsDlg.className = 'bdp-fs';
    fsDlg.setAttribute('aria-label', 'Drawing');
    fsDlg.innerHTML = '<div class="bdp-fs__top"><strong data-bdp-fs-title>Drawing</strong><button type="button" class="bdp-fs__close" data-bdp-fs-close aria-label="Close">×</button></div><div class="bdp-fs__stage" data-bdp-fs-stage><svg data-bdp-fs-svg role="img"></svg></div><div class="bdp-fs__zoom"><button type="button" data-bdp-z="out" aria-label="Zoom out">−</button><button type="button" data-bdp-z="reset">Reset</button><button type="button" data-bdp-z="in" aria-label="Zoom in">+</button></div>';
    document.body.appendChild(fsDlg);

    const helpLinks = `<div class="bdp-help"><p><strong>Need help matching an existing cubicle?</strong> Send us photos of the cubicle and its hardware and we will identify the parts and colour, and price them for you.</p>
      <div class="bdp-help__acts"><a class="bdp-btn bdp-btn--small" href="${esc(cfg.whatsapp)}" target="_blank" rel="noopener">WhatsApp photos</a><a class="bdp-btn bdp-btn--small" href="${esc(cfg.email)}">Email photos</a><a class="bdp-btn bdp-btn--small" href="tel:${esc(cfg.phoneTel)}">Call ${esc(cfg.phone)}</a></div></div>`;

    function stepHead(n, title) {
      return `<p class="bdp-kicker">Step ${n} of 5${S.ed && !S.ed.isNew ? ' · editing ' + esc(TYPES[S.ed.type].label.toLowerCase()) : ''}</p><h2 class="bdp-title" id="bdp-h" tabindex="-1">${title}</h2>`;
    }
    function drawingFig(id) {
      return `<button type="button" class="bdp-fig" data-bdp-fs="1" aria-label="Open the drawing full screen"><svg class="bdp-fig__svg" data-bdp-svg="${id}" role="img"></svg><span class="bdp-fig__exp" aria-hidden="true">⤢</span></button>`;
    }
    function edDims() { const ed = S.ed; return { h: Number(ed.l) || 0, w: Number(ed.w) || 0, live: Number(ed.l) > 0 && Number(ed.w) > 0 }; }
    function edHw() { const ed = S.ed; return Object.assign({ mat: ed.mat || 'cgl' }, ed.tr && document.contains(ed.tr) ? A.rowHw(ed.tr) : (ed.hw || {})); }
    function paintDrawings() {
      if (!S.ed || !window.BW_PART_DRAW) return;
      app.querySelectorAll('[data-bdp-svg]').forEach(svg => {
        window.BW_PART_DRAW(svg, S.ed.type, edDims(), edHw(), FIT[S.ed.type]);
        svg.setAttribute('aria-label', TYPES[S.ed.type].label + ' drawing' + (edDims().live ? ', ' + S.ed.w + ' mm wide by ' + S.ed.l + ' mm high' : ', example size'));
      });
    }

    function renderStep1() {
      const ed = S.ed;
      body.innerHTML = stepHead(1, 'What are you replacing?') +
        `<div class="bdp-types" role="radiogroup" aria-label="Part">` +
        ['door', 'partition', 'pilaster'].map(t => `<button type="button" class="bdp-type${ed.type === t ? ' is-on' : ''}" role="radio" aria-checked="${ed.type === t}" data-bdp-type="${t}">${TYPE_ICON[t]}<strong>${TYPES[t].label}</strong><small>Up to ${TYPES[t].maxW} × ${TYPES[t].maxH}mm</small></button>`).join('') +
        `</div><p class="bdp-note">You can add more doors, partitions and pilasters to the same order afterwards.</p>` +
        `<div class="bdp-nav">${components().length ? '<button type="button" class="bdp-btn" data-bdp-go="review">Back to your order</button>' : ''}<button type="button" class="bdp-btn bdp-btn--primary" data-bdp-go="2">Continue</button></div>` +
        `<button type="button" class="bdp-link bdp-import" data-bdp-act="import">Import an existing cutting list (CSV or Excel)</button>` +
        helpLinks;
    }
    function swatchList() {
      const q = S.q.trim().toLowerCase();
      return COLOURS.filter(c => (S.brand === 'All' || brandOf(c) === S.brand) && (!q || (c.name + ' ' + c.code + ' ' + c.manu + ' ' + (c.group || '')).toLowerCase().includes(q)));
    }
    function renderSwatches() {
      const grid = app.querySelector('[data-bdp-swatches]');
      if (!grid) return;
      const list = swatchList(), cap = (S.all || S.q) ? list.length : 15;
      grid.innerHTML = list.slice(0, cap).map(c => `<button type="button" class="bdp-sw${S.ed.colour === c.key ? ' is-on' : ''}" role="radio" aria-checked="${S.ed.colour === c.key}" data-bdp-colour="${c.key}">${c.img ? `<img src="${esc(c.img)}" alt="" loading="lazy" width="120" height="120">` : '<span class="bdp-sw__blank"></span>'}<span class="bdp-sw__n">${esc(c.name)}</span><span class="bdp-sw__m">${esc([c.code, brandOf(c)].filter(Boolean).join(' · '))}</span></button>`).join('');
      app.querySelector('[data-bdp-none]').hidden = list.length > 0;
      const more = app.querySelector('[data-bdp-act="more"]');
      more.hidden = !!S.q || list.length <= 15;
      more.textContent = S.all ? 'Show fewer colours' : 'Show all ' + list.length + ' colours';
      app.querySelectorAll('[data-bdp-brand]').forEach(b => b.setAttribute('aria-pressed', String(b.getAttribute('data-bdp-brand') === S.brand)));
    }
    function renderStep2() {
      const ed = S.ed;
      body.innerHTML = stepHead(2, 'Choose the material and colour') +
        `<span class="bdp-label" id="bdp-mat-l">Material</span><div class="bdp-hwchoice bdp-mats" role="radiogroup" aria-labelledby="bdp-mat-l">` +
        Object.keys(MATS).map(k => `<button type="button" class="bdp-opt${ed.mat === k ? ' is-on' : ''}" role="radio" aria-checked="${ed.mat === k}" data-bdp-mat="${k}"><strong>${esc(matOf(k).label)}</strong><small>${esc(MAT_NOTE[k] || '')}</small></button>`).join('') +
        `</div><p class="bdp-note">Match what your cubicles are made of: compact grade is about 13mm thick with a solid black core, MFC is 18mm chipboard with a plastic edge strip. Not sure? <a href="${esc(cfg.whatsapp)}" target="_blank" rel="noopener">Send us a photo</a> of a panel edge.</p>` +
        `<span class="bdp-label bdp-label--gap">Colour</span>` +
        `<label class="bdp-vh" for="bdp-q">Search colours</label><input id="bdp-q" class="bdp-input bdp-search" type="search" placeholder="Search colour name or code" value="${esc(S.q)}" autocomplete="off" data-bdp-q>` +
        (BRANDS.length > 1 ? `<div class="bdp-chips">${['All'].concat(BRANDS).map(b => `<button type="button" class="bdp-chip" data-bdp-brand="${esc(b)}" aria-pressed="${S.brand === b}">${esc(b)}</button>`).join('')}</div>` : '') +
        `<div class="bdp-sws" role="radiogroup" aria-label="Colour" data-bdp-swatches></div><p class="bdp-note" data-bdp-none hidden>No colours match that search.</p>` +
        `<button type="button" class="bdp-link" data-bdp-act="more" hidden></button>` +
        `<p class="bdp-err" data-bdp-err="colour" role="alert" hidden></p>` +
        `<div class="bdp-match"><p><strong>Matching an existing cubicle?</strong> Colours vary between batches and photos, so we can’t promise an exact match from a picture. Order the sample chart${cfg.sampleMoney ? ' (' + esc(cfg.sampleMoney) + ' exc VAT)' : ''} to check against your cubicles, or send us photos and we’ll advise.</p>` +
        `<div class="bdp-help__acts"><button type="button" class="bdp-btn bdp-btn--small" data-bdp-act="sample">Order sample chart</button><a class="bdp-btn bdp-btn--small" href="${esc(cfg.whatsapp)}" target="_blank" rel="noopener">Send photos</a></div><p class="bdp-status" data-bdp-sample-status role="status"></p></div>` +
        `<div class="bdp-nav"><button type="button" class="bdp-btn" data-bdp-go="1">Back</button><button type="button" class="bdp-btn bdp-btn--primary" data-bdp-go="3">Continue</button></div>`;
      renderSwatches();
    }
    function renderStep3() {
      const ed = S.ed, pt = TYPES[ed.type];
      body.innerHTML = stepHead(3, 'Enter the ' + pt.label.toLowerCase() + ' size') +
        drawingFig('size') +
        `<p class="bdp-measure"><strong>How to measure:</strong> ${MEASURE[ed.type]}</p>` +
        `<div class="bdp-fields">` +
        `<div class="bdp-field"><label class="bdp-label" for="bdp-w">Width (mm)</label><input id="bdp-w" class="bdp-input" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" value="${esc(ed.w)}" data-bdp-in="w"><p class="bdp-hint">${pt.minW}–${pt.maxW}mm</p></div>` +
        `<div class="bdp-field"><label class="bdp-label" for="bdp-l">Height (mm)</label><input id="bdp-l" class="bdp-input" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" value="${esc(ed.l)}" data-bdp-in="l"><p class="bdp-hint">${pt.minH}–${pt.maxH}mm</p></div>` +
        `</div><p class="bdp-err" data-bdp-err="size" role="alert" hidden></p>` +
        `<div class="bdp-field bdp-field--qty"><span class="bdp-label" id="bdp-q-l">Quantity</span><div class="bdp-qty"><button type="button" data-bdp-qty="-1" aria-label="Fewer">−</button><input class="bdp-input" type="text" inputmode="numeric" pattern="[0-9]*" aria-labelledby="bdp-q-l" value="${esc(ed.q)}" data-bdp-in="q"><button type="button" data-bdp-qty="1" aria-label="More">+</button></div></div>` +
        `<details class="bdp-more"${ed.name ? ' open' : ''}><summary>Name this ${pt.label.toLowerCase()} <small>(optional, e.g. “Cubicle 3”)</small></summary><input class="bdp-input" type="text" maxlength="80" value="${esc(ed.name)}" data-bdp-in="name" aria-label="Name"></details>` +
        `<div class="bdp-nav"><button type="button" class="bdp-btn" data-bdp-go="2">Back</button><button type="button" class="bdp-btn bdp-btn--primary" data-bdp-go="4">Continue</button></div>`;
      paintDrawings();
      showSizeError();
    }
    function hwControls() {
      const ed = S.ed, h = edHw(), unit = A.hwUnitPence();
      if (ed.type === 'door') {
        // Compact doors get the 13mm inward-opening pack (fixed contents, supplied loose);
        // MFC doors the drilled-and-fitted pack with hinge count and bolt chosen here.
        const mfc = h.mat === 'mfc';
        const blocked = !mfc && h.opening === 'out';
        // Nothing is pre-selected: the customer answers Yes or No for every door.
        const on = h.ans === 'yes' && !blocked;
        const no = h.ans === 'no' || (h.ans === 'yes' && blocked);
        const sideSeg = `<div class="bdp-seg"><span class="bdp-label">Hinge side <em>(viewed from outside, door closed)</em></span><div>${['left', 'right'].map(v => `<button type="button" data-bdp-hw="side" data-v="${v}" class="${h.side === v ? 'is-on' : ''}" aria-pressed="${h.side === v}">${v === 'left' ? 'Left' : 'Right'}</button>`).join('')}</div></div>`;
        const openSeg = `<div class="bdp-seg"><span class="bdp-label">Door opens</span><div>${[['in', 'Inward'], ['out', 'Outward']].map(([v, l]) => `<button type="button" data-bdp-hw="opening" data-v="${v}" class="${h.opening === v ? 'is-on' : ''}" aria-pressed="${h.opening === v}">${l}</button>`).join('')}</div></div>`;
        return `<span class="bdp-label" id="bdp-hwq-l">Do you need a hardware pack for this door?</span><div class="bdp-hwchoice" role="radiogroup" aria-labelledby="bdp-hwq-l">
            <button type="button" class="bdp-opt${on ? ' is-on' : ''}" role="radio" aria-checked="${on}" data-bdp-hw="enabled" data-v="true"${blocked ? ' disabled' : ''}><strong>Yes, add the hardware pack · ${money((mfc ? unit.doorPackMfc : unit.doorPack) || 0)} per door</strong><small>${mfc ? 'Hinges and indicator bolt supplied, drilled and fitted ready to hang.' : (blocked ? 'For inward-opening doors only.' : 'For 13mm inward-opening doors: a pair of gravity hinges, slide indicator bolt and keep, and a coat hook / buffer. Supplied loose to fit on site; the door comes undrilled.')}</small></button>
            <button type="button" class="bdp-opt${no ? ' is-on' : ''}" role="radio" aria-checked="${no}" data-bdp-hw="enabled" data-v="false"><strong>No, door only</strong><small>Supplied cut and edged, undrilled. Re-use your existing hinges and indicator bolt.</small></button></div>` +
          (blocked ? openSeg + `<p class="bdp-warn">This pack’s keep is for inward-opening doors, so it can’t go on a door that opens outward. <a href="${esc(cfg.email)}">Ask us</a> about outward-opening hardware.</p>` : '') +
          (on && !mfc ? sideSeg + openSeg : '') +
          (on && mfc ? sideSeg + openSeg + `
          <div class="bdp-seg"><span class="bdp-label">Number of hinges <em>(2 included, ${money(unit.extraHinge || 1500)} for each extra)</em></span><div>${[2, 3, 4].map(v => `<button type="button" data-bdp-hw="count" data-v="${v}" class="${h.count === v ? 'is-on' : ''}" aria-pressed="${h.count === v}">${v}<small>${v === 2 ? 'included' : '+' + money((v - 2) * (unit.extraHinge || 1500))}</small></button>`).join('')}</div></div>
          <label class="bdp-check"><input type="checkbox" data-bdp-hw="bolt"${h.bolt ? ' checked' : ''}> Indicator bolt &amp; receiver <em>(opposite the hinges)</em></label>` : '') +
          (on ? `<p class="bdp-note">The drawing shows where each item goes; exact positions follow our standard hardware set. Need it to line up with existing frames or hardware? Send us photos first.</p>` : '');
      }
      const isPart = ed.type === 'partition';
      const yes = h.ans === 'yes';
      const ask = `<span class="bdp-label" id="bdp-hwq-l">Do you need hardware for this ${ed.type}?</span><div class="bdp-hwchoice" role="radiogroup" aria-labelledby="bdp-hwq-l">
          <button type="button" class="bdp-opt${yes ? ' is-on' : ''}" role="radio" aria-checked="${yes}" data-bdp-hw="ans" data-v="yes"><strong>Yes, add hardware</strong><small>Channel brackets, a support leg or both.</small></button>
          <button type="button" class="bdp-opt${h.ans === 'no' ? ' is-on' : ''}" role="radio" aria-checked="${h.ans === 'no'}" data-bdp-hw="ans" data-v="no"><strong>No hardware</strong><small>Panel only, cut and edged, undrilled. Re-use your existing fixings.</small></button></div>`;
      if (!yes) return ask;
      return ask + `<label class="bdp-check bdp-check--big"><input type="checkbox" data-bdp-hw="brackets"${h.brackets ? ' checked' : ''}><span><strong>Channel brackets · ${money(isPart ? unit.partitionBrackets || 0 : unit.pilasterBrackets || 0)} per ${ed.type}</strong><small>${isPart ? '2 packs of 3 (6 brackets) with fixings: one set for the wall end, one for the pilaster end.' : '1 pack of 3 brackets with fixings, on the edge the partition fixes to.'}</small></span></label>` +
        (!isPart && h.brackets ? `<div class="bdp-seg"><span class="bdp-label">Bracket side <em>(the edge the partition fixes to)</em></span><div>${['left', 'right'].map(v => `<button type="button" data-bdp-hw="bracketSide" data-v="${v}" class="${h.bracketSide === v ? 'is-on' : ''}" aria-pressed="${h.bracketSide === v}">${v === 'left' ? 'Left' : 'Right'}</button>`).join('')}</div></div>` : '') +
        `<label class="bdp-check bdp-check--big"><input type="checkbox" data-bdp-hw="leg"${h.leg ? ' checked' : ''}><span><strong>Support leg · ${money(unit.leg || 0)} per ${ed.type}</strong><small>One adjustable floor leg with fixings${isPart ? ', at the front (door) end' : ''}.</small></span></label>` +
        (isPart && h.leg ? `<div class="bdp-seg"><span class="bdp-label">Leg position <em>(viewed from the front)</em></span><div>${['left', 'right'].map(v => `<button type="button" data-bdp-hw="legSide" data-v="${v}" class="${h.legSide === v ? 'is-on' : ''}" aria-pressed="${h.legSide === v}">${v === 'left' ? 'Left' : 'Right'}</button>`).join('')}</div></div>` : '') +
        (!h.brackets && !h.leg ? `<p class="bdp-warn">Tick at least one item, or choose No hardware.</p>` : '');
    }
    // Step 4 can't be left for the review until this part's hardware is answered.
    function hwProblem() { return A.hwAnswered && !A.hwAnswered(S.ed.type, edHw()) ? 'Choose Yes or No for the hardware on this ' + TYPES[S.ed.type].label.toLowerCase() + '.' : ''; }
    function showHwError() {
      const el = app.querySelector('[data-bdp-err="hw"]');
      if (!el) return;
      const p = S.hwNag ? hwProblem() : '';
      el.textContent = p; el.hidden = !p;
    }
    function renderStep4() {
      const ed = S.ed;
      body.innerHTML = stepHead(4, 'Hardware for this ' + TYPES[ed.type].label.toLowerCase()) + drawingFig('hw') + `<div class="bdp-hw" data-bdp-hwbox>${hwControls()}</div>` +
        `<p class="bdp-err" data-bdp-err="hw" role="alert" hidden></p>` +
        `<div class="bdp-nav"><button type="button" class="bdp-btn" data-bdp-go="3">Back</button><button type="button" class="bdp-btn bdp-btn--primary" data-bdp-go="review">Save and review</button></div>` +
        `<p class="bdp-note">Not sure what hardware your cubicles use? <a href="${esc(cfg.whatsapp)}" target="_blank" rel="noopener">Send us photos</a> and we’ll advise before you order.</p>`;
      paintDrawings();
      showHwError();
    }
    function renderReview() {
      const comps = components(), sm = A.summary(), gs = A.groups();
      let html = `<p class="bdp-kicker">Step 5 of 5</p><h2 class="bdp-title" id="bdp-h" tabindex="-1">Your order</h2>`;
      if (!comps.length) html += `<p class="bdp-note">Nothing added yet.</p>`;
      gs.forEach(g => {
        const mine = comps.filter(c => c.gid === g.id);
        if (!mine.length) return;
        const t = g.totals || {};
        const m = matOf(g.material);
        html += `<section class="bdp-group"><header class="bdp-group__h"><span class="bdp-group__chip" style="${g.colour.img ? `background-image:url('${esc(g.colour.img)}')` : ''}"></span><span><strong>${esc(g.colour.name || 'No colour')}</strong> <small>${esc([m.label, g.colour.code, g.colour.manu].filter(Boolean).join(' · '))}</small></span><span class="bdp-group__p">${t.hasPanels ? money(t.totalCost) : g.overCap ? 'Quote' : '—'}</span></header>` +
          `<p class="bdp-group__n">${t.hasPanels ? plural(t.panels, 'part') + ' cut from ' + plural(t.sheets, 'shared sheet') + ': price covers board, cutting, ' + (g.material === 'mfc' ? 'edging' : 'machining') + ' and delivery.' : ''}</p>`;
        mine.forEach(c => {
          const label = TYPES[c.d.type].label, note = window.BW_ROW_HW ? window.BW_ROW_HW.note(c.d.type, c.hw) : '';
          html += `<article class="bdp-card${c.problem || c.hwTodo ? ' is-bad' : ''}"><div class="bdp-card__top"><span class="bdp-card__ico">${TYPE_ICON[c.d.type]}</span><div><strong>${esc(c.d.name)}</strong> <span class="bdp-tag">${label}</span><p>${c.d.w || '—'} × ${c.d.l || '—'}mm (W × H) · Qty ${c.d.q}</p><p class="bdp-card__muted">${esc(m.edgeNote || '')}</p>${c.hwTodo ? `<p class="bdp-err">Hardware: choose Yes or No</p>` : `<p class="bdp-card__muted">${note ? esc(note) : 'No hardware, panel only, undrilled'}</p>`}${c.problem ? `<p class="bdp-err">${esc(c.problem)}</p>` : ''}</div></div>` +
            `<div class="bdp-card__acts">${c.hwTodo ? '<button type="button" class="bdp-link" data-bdp-card="hw">Choose hardware</button>' : ''}<button type="button" class="bdp-link" data-bdp-card="edit">Edit</button><button type="button" class="bdp-link" data-bdp-card="dup">Duplicate</button><button type="button" class="bdp-link bdp-link--del" data-bdp-card="del">Remove</button></div></article>`;
        });
        html += `</section>`;
      });
      const lines = window.BW_HW_LINES ? window.BW_HW_LINES() : [];
      if (lines.length) html += `<section class="bdp-group"><header class="bdp-group__h"><strong>Hardware</strong><span class="bdp-group__p">${money(sm.hwPence)}</span></header><ul class="bdp-hwlist">${lines.map(l => `<li>${l.qty} × ${esc(l.title)} <small>${esc(l.detail)}</small></li>`).join('')}</ul></section>`;
      html += `<button type="button" class="bdp-add" data-bdp-act="add">+ Add another door, partition or pilaster</button>`;
      if (sm.overCap) html += `<div class="bdp-cap"><p><strong>This order is above the ${money((A.cap || 0) * 100)} we can price online.</strong> Our sales team will quote it directly.</p><button type="button" class="bdp-btn bdp-btn--block" data-bdp-act="quote">Request a quote</button></div>`;
      html += `<div class="bdp-total"><div><span>Total</span><strong>${sm.priced ? money(sm.totalPence) : '—'} <small>exc VAT</small></strong></div><div class="bdp-total__vat">${sm.priced ? money(Math.round(sm.totalPence * 1.2)) + ' inc VAT' : (sm.error ? 'Fix the highlighted sizes to see your price.' : '')}</div></div>` +
        (sm.priced && sm.hwPending ? `<p class="bdp-err">Choose Yes or No for the hardware on ${sm.hwPending === 1 ? 'the part' : 'the ' + sm.hwPending + ' parts'} marked above before adding to basket.</p>` : '') +
        `<button type="button" class="bdp-btn bdp-btn--primary bdp-btn--block${sm.priced && !sm.hwPending ? '' : ' is-off'}" data-bdp-act="buy" aria-disabled="${!(sm.priced && !sm.hwPending)}">Add to basket</button><p class="bdp-status" data-bdp-status role="status"></p>` +
        `<p class="bdp-note">Free UK mainland delivery${cfg.lead ? ' · usually ' + esc(cfg.lead) : ''}. Made in our Bolton workshop.</p>` +
        `<button type="button" class="bdp-link bdp-import" data-bdp-act="import">Import an existing cutting list</button>`;
      body.innerHTML = html;
    }

    function render(focus) {
      app.dataset.step = S.review ? '5' : String(S.step);
      app.querySelectorAll('[data-p]').forEach(li => { const n = Number(li.getAttribute('data-p')), cur = S.review ? 5 : S.step; li.classList.toggle('is-cur', n === cur); li.classList.toggle('is-done', n < cur); });
      if (S.review) renderReview();
      else if (S.step === 1) renderStep1();
      else if (S.step === 2) renderStep2();
      else if (S.step === 3) renderStep3();
      else renderStep4();
      renderBar();
      if (focus) { app.scrollIntoView({ block: 'start' }); const h = app.querySelector('#bdp-h'); if (h) h.focus({ preventScroll: true }); }
    }
    function renderBar() {
      const sm = A.summary();
      const price = app.querySelector('[data-bdp-bar-price]'), sub = app.querySelector('[data-bdp-bar-sub]'), btn = app.querySelector('[data-bdp-bar-btn]');
      price.textContent = sm.priced ? money(sm.totalPence) : sm.overCap ? 'Quote' : '—';
      const n = components().length;
      sub.textContent = sm.priced ? 'exc VAT · ' + plural(n, 'part') : (n ? 'Enter valid sizes' : 'Start your order');
      btn.textContent = S.review ? 'Add to basket' : S.step === 4 ? 'Save and review' : 'Continue';
      btn.classList.toggle('is-off', S.review && !(sm.priced && !sm.hwPending));
    }

    /* ------------------------------ Validation ------------------------------ */
    function sizeProblem() {
      const ed = S.ed;
      const gid = ed.tr ? gidOfRow(ed.tr) : (A.groups()[0] || {}).id;
      const l = Number(ed.l), w = Number(ed.w);
      if (!/^\d*$/.test(String(ed.l)) || !/^\d*$/.test(String(ed.w))) return 'Enter whole millimetres, numbers only.';
      return A.rowProblem(gid, ed.type, l, w);
    }
    function showSizeError() {
      const el = app.querySelector('[data-bdp-err="size"]');
      if (!el) return;
      const ed = S.ed;
      const show = S.tried || (S.touched.l && S.touched.w) || (ed.l && ed.w);
      const p = show ? sizeProblem() : '';
      el.textContent = p; el.hidden = !p;
      app.querySelectorAll('[data-bdp-in="l"], [data-bdp-in="w"]').forEach(i => i.setAttribute('aria-invalid', p ? 'true' : 'false'));
    }

    /* ------------------------------ Navigation ------------------------------ */
    function go(to) {
      const ed = S.ed;
      if (to === 'review') {
        if (ed && S.step === 3) { S.tried = true; if (sizeProblem()) { showSizeError(); return; } }
        if (ed && S.step === 4 && !S.review && hwProblem()) { S.hwNag = true; showHwError(); const e = app.querySelector('[data-bdp-err="hw"]'); if (e) e.scrollIntoView({ block: 'center' }); return; }
        if (ed && ed.tr) writeSizes();
        S.review = true; S.ed = null; S.tried = false; S.hwNag = false;
        A.reprice();
        render(true);
        return;
      }
      const n = Number(to);
      if (n > S.step) {
        if (S.step === 2 && !ed.colour) { const e = app.querySelector('[data-bdp-err="colour"]'); if (e) { e.textContent = 'Choose a colour to continue.'; e.hidden = false; } return; }
        if (S.step === 3) { S.tried = true; if (sizeProblem()) { showSizeError(); const i = app.querySelector('[data-bdp-in="w"]'); if (i) i.focus(); return; } }
      }
      if (n === 3 && ed.colour) ensureRow();
      if (S.step === 3 && ed.tr) writeSizes();
      S.step = n; S.tried = false;
      render(true);
    }
    function startNew(type) {
      S.review = false; S.step = 1; S.ed = newEdit(type); S.touched = {}; S.tried = false; S.hwNag = false;
      render(true);
    }
    function editRow(tr, step) {
      const d = A.rowData(tr);
      const g = A.groups().find(x => x.id === gidOfRow(tr));
      S.ed = { tr, isNew: false, type: d.type, mat: (g && g.material) || 'cgl', colour: g ? colourKeyOf(g.colour) : '', l: d.l ? String(d.l) : '', w: d.w ? String(d.w) : '', q: String(d.q || 1), name: (tr.querySelector('.pb-name') || {}).value || '', hw: null };
      S.review = false; S.step = step || 3; S.touched = { l: true, w: true }; S.tried = false;
      render(true);
    }

    /* ------------------------------ Events ------------------------------ */
    app.addEventListener('click', e => {
      const t = e.target.closest('button, a');
      if (!t || !app.contains(t)) return;
      const ed = S.ed;
      if (t.hasAttribute('data-bdp-type')) {
        ed.type = t.getAttribute('data-bdp-type');
        if (ed.tr && document.contains(ed.tr)) A.setRow(ed.tr, { type: ed.type });
        render();
        return;
      }
      if (t.hasAttribute('data-bdp-mat')) {
        ed.mat = t.getAttribute('data-bdp-mat');
        // An existing component moves to the group for its new material.
        if (ed.colour && ed.tr && document.contains(ed.tr)) ensureRow();
        render();
        return;
      }
      if (t.hasAttribute('data-bdp-colour')) {
        ed.colour = t.getAttribute('data-bdp-colour');
        if (!ed.isNew || ed.tr) ensureRow();
        go(3);
        return;
      }
      if (t.hasAttribute('data-bdp-brand')) { S.brand = t.getAttribute('data-bdp-brand'); S.all = false; renderSwatches(); return; }
      if (t.hasAttribute('data-bdp-go')) { go(t.getAttribute('data-bdp-go')); return; }
      if (t.hasAttribute('data-bdp-fs')) { openFs(t); return; }
      if (t.hasAttribute('data-bdp-qty')) {
        ed.q = String(Math.max(1, (parseInt(ed.q, 10) || 1) + Number(t.getAttribute('data-bdp-qty'))));
        const i = app.querySelector('[data-bdp-in="q"]'); if (i) i.value = ed.q;
        if (ed.tr) writeSizes();
        renderBar();
        return;
      }
      if (t.hasAttribute('data-bdp-hw') && t.tagName === 'BUTTON') {
        const k = t.getAttribute('data-bdp-hw'); let v = t.getAttribute('data-v');
        if (v === 'true' || v === 'false') v = v === 'true'; else if (/^\d+$/.test(v)) v = Number(v);
        const patch = { [k]: v };
        // The compact-grade pack's keep is inward-only, so an outward door drops it.
        if (k === 'opening' && v === 'out' && edHw().mat !== 'mfc') patch.enabled = false;
        if (ed.tr) A.setRowHw(ed.tr, patch);
        app.querySelector('[data-bdp-hwbox]').innerHTML = hwControls(); showHwError();
        paintDrawings(); renderBar();
        return;
      }
      if (t.hasAttribute('data-bdp-card')) {
        const art = t.closest('.bdp-card');
        const comps = components();
        const idx = Array.from(app.querySelectorAll('.bdp-card')).indexOf(art);
        const c = comps[idx];
        if (!c) return;
        const act = t.getAttribute('data-bdp-card');
        if (act === 'edit') editRow(c.tr, 3);
        else if (act === 'hw') { S.hwNag = false; editRow(c.tr, 4); }
        else if (act === 'dup') { A.duplicateRow(c.tr); render(); }
        else if (act === 'del') { A.removeRow(c.tr); cleanEmptyGroups(); A.reprice(); if (!components().length) startNew(A.lead); else render(); }
        return;
      }
      const act = t.getAttribute('data-bdp-act');
      if (act === 'more') { S.all = !S.all; renderSwatches(); }
      else if (act === 'add') startNew(A.lead);
      else if (act === 'quote') A.openQuote(t);
      else if (act === 'buy') buy();
      else if (act === 'import') importList();
      else if (act === 'sample') addSample(t);
    });
    app.addEventListener('input', e => {
      const t = e.target;
      if (t.matches('[data-bdp-q]')) { S.q = t.value; renderSwatches(); return; }
      if (t.matches('[data-bdp-in]')) {
        const k = t.getAttribute('data-bdp-in');
        S.ed[k] = t.value.trim();
        if (S.ed.tr) writeSizes();
        if (k === 'l' || k === 'w') { paintDrawings(); showSizeError(); }
        renderBar();
      }
    });
    app.addEventListener('change', e => {
      const t = e.target;
      if (t.matches('input[type="checkbox"][data-bdp-hw]')) {
        if (S.ed.tr) A.setRowHw(S.ed.tr, { [t.getAttribute('data-bdp-hw')]: t.checked });
        app.querySelector('[data-bdp-hwbox]').innerHTML = hwControls(); showHwError();
        paintDrawings(); renderBar();
      }
    });
    app.addEventListener('focusout', e => { if (e.target.matches('[data-bdp-in="l"], [data-bdp-in="w"]')) { S.touched[e.target.getAttribute('data-bdp-in')] = true; showSizeError(); } });
    app.addEventListener('focusin', e => { if (e.target.matches('input')) app.classList.add('is-typing'); });
    app.addEventListener('focusout', () => setTimeout(() => { if (!app.contains(document.activeElement) || !document.activeElement.matches('input')) app.classList.remove('is-typing'); }, 0));
    app.querySelector('[data-bdp-bar-btn]').addEventListener('click', () => {
      if (S.review) buy();
      else if (S.step === 4) go('review');
      else go(S.step + 1);
    });

    function setStatus(msg) { const el = app.querySelector('[data-bdp-status]'); if (el) el.textContent = msg || ''; }
    async function buy() {
      const sm = A.summary();
      if (!sm.priced) { setStatus(sm.overCap ? 'This order is above the online limit. Please request a quote.' : 'Please fix the highlighted parts first.'); return; }
      // A part without a hardware answer: open its hardware step instead of adding.
      const todo = sm.hwPending ? components().find(c => c.hwTodo) : null;
      if (todo) { S.hwNag = true; editRow(todo.tr, 4); return; }
      setStatus('Adding to your basket…');
      A.addToCart();
      // The engine reports its result in its own status line.
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 200));
        const st = A.cartStatus();
        if (/added|could not|error|unavailable/i.test(st)) { setStatus(st); return; }
      }
    }
    function importList() {
      const inp = A.importInput();
      if (!inp) return;
      const done = () => setTimeout(() => { S.review = true; S.ed = null; A.reprice(); render(true); }, 900);
      inp.addEventListener('change', done, { once: true });
      inp.click();
    }
    function addSample(btn) {
      // The page's own sample-chart button adds it at its real price.
      const src = document.getElementById('pb-addsample');
      const out = app.querySelector('[data-bdp-sample-status]');
      if (!src) return;
      btn.textContent = 'Adding…';
      src.click();
      setTimeout(() => { btn.textContent = 'Order sample chart'; const st = document.getElementById('pb-sample-status'); if (out) out.textContent = st ? st.textContent : ''; }, 1500);
    }

    /* ------------------------------ Full-screen drawing ------------------------------ */
    let zoom = 1, panX = 0, panY = 0, back = null;
    const fsSvg = fsDlg.querySelector('[data-bdp-fs-svg]'), fsStage = fsDlg.querySelector('[data-bdp-fs-stage]');
    function applyZoom() { fsSvg.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`; }
    function openFs(from) {
      if (!S.ed || !window.BW_PART_DRAW) return;
      back = from; zoom = 1; panX = 0; panY = 0;
      window.BW_PART_DRAW(fsSvg, S.ed.type, edDims(), edHw());
      fsDlg.querySelector('[data-bdp-fs-title]').textContent = TYPES[S.ed.type].label + (edDims().live ? ' · ' + S.ed.w + ' × ' + S.ed.l + 'mm' : ' · example size');
      applyZoom();
      if (fsDlg.showModal) fsDlg.showModal(); else fsDlg.setAttribute('open', '');
    }
    fsDlg.addEventListener('close', () => { if (back) back.focus(); });
    fsDlg.addEventListener('click', e => {
      const b = e.target.closest('button'); if (!b) return;
      if (b.hasAttribute('data-bdp-fs-close')) fsDlg.close();
      const z = b.getAttribute('data-bdp-z');
      if (z) { zoom = z === 'reset' ? 1 : Math.max(1, Math.min(5, zoom * (z === 'in' ? 1.4 : 1 / 1.4))); if (zoom === 1) { panX = 0; panY = 0; } applyZoom(); }
    });
    const pts = new Map(); let pinch0 = 0, zoom0 = 1, drag0 = null;
    fsStage.addEventListener('pointerdown', e => { fsStage.setPointerCapture(e.pointerId); pts.set(e.pointerId, { x: e.clientX, y: e.clientY }); drag0 = { x: e.clientX - panX, y: e.clientY - panY }; if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch0 = Math.hypot(a.x - b.x, a.y - b.y); zoom0 = zoom; } });
    fsStage.addEventListener('pointermove', e => { if (!pts.has(e.pointerId)) return; pts.set(e.pointerId, { x: e.clientX, y: e.clientY }); if (pts.size === 2 && pinch0) { const [a, b] = [...pts.values()]; zoom = Math.max(1, Math.min(5, zoom0 * Math.hypot(a.x - b.x, a.y - b.y) / pinch0)); } else if (pts.size === 1 && zoom > 1 && drag0) { panX = e.clientX - drag0.x; panY = e.clientY - drag0.y; } applyZoom(); });
    const up = e => { pts.delete(e.pointerId); if (pts.size < 2) pinch0 = 0; if (!pts.size) drag0 = null; };
    fsStage.addEventListener('pointerup', up); fsStage.addEventListener('pointercancel', up);

    /* ------------------------------ Supporting content folds ------------------------------ */
    function fold() {
      pbRoot.querySelectorAll('.pb-info, .pb-description').forEach(sec => {
        if (sec.dataset.bdpFold) return;
        sec.dataset.bdpFold = '1';
        const h = sec.querySelector('h2, h3');
        const btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'bdp-foldbtn'; btn.setAttribute('aria-expanded', 'false');
        btn.textContent = h ? h.textContent.trim() : 'More information';
        sec.before(btn);
        sec.classList.add('bdp-folded');
        btn.addEventListener('click', () => { const open = sec.classList.toggle('bdp-folded') === false; btn.setAttribute('aria-expanded', String(open)); });
      });
    }

    /* ------------------------------ Mode ------------------------------ */
    let mounted = false;
    function mode() {
      const on = PHONE.matches;
      pbRoot.classList.toggle('bdp-on', on);
      if (on && !mounted) {
        mounted = true;
        fold();
        if (components().length) { S.review = true; S.ed = null; } else S.ed = newEdit(A.lead);
        render();
      }
    }
    PHONE.addEventListener && PHONE.addEventListener('change', mode);
    mode();

    /* ------------------------------ Desktop: sticky order summary ------------------------------ */
    // The desktop cutting list stays as it is; this keeps the running total, part count and
    // Add to basket in view while a long list is entered.
    const side = document.querySelector('.pb-sidebar');
    let deskCard = null;
    if (side) {
      deskCard = document.createElement('div');
      deskCard.className = 'bdp-desk pb-card';
      side.prepend(deskCard);
    }
    function renderDesk() {
      if (!deskCard) return;
      const sm = A.summary(), comps = components();
      const counts = { door: 0, partition: 0, pilaster: 0 };
      comps.forEach(c => { if (counts[c.d.type] !== undefined) counts[c.d.type] += c.d.q; });
      const parts = Object.keys(counts).filter(k => counts[k]).map(k => plural(counts[k], TYPES[k].label.toLowerCase())).join(', ');
      const bad = comps.filter(c => c.problem);
      deskCard.innerHTML = '<h3 class="bdp-desk__h">Your order</h3>' +
        '<p class="bdp-desk__parts">' + (parts || 'No parts yet') + '</p>' +
        (sm.priced ? '<div class="bdp-desk__row"><span>Panels</span><strong>' + money(sm.panelsPence) + '</strong></div>' + (sm.hwPence ? '<div class="bdp-desk__row"><span>Hardware</span><strong>' + money(sm.hwPence) + '</strong></div>' : '') : '') +
        '<div class="bdp-desk__total"><span>Total</span><strong>' + (sm.priced ? money(sm.totalPence) : '—') + ' <small>exc VAT</small></strong></div>' +
        (sm.priced ? '<p class="bdp-desk__vat">' + money(Math.round(sm.totalPence * 1.2)) + ' inc VAT</p>' : '') +
        (bad.length ? '<p class="bdp-desk__err">' + esc(bad[0].d.name + ': ' + bad[0].problem) + (bad.length > 1 ? ' (+' + (bad.length - 1) + ' more)' : '') + '</p>' : '') +
        (sm.overCap ? '<p class="bdp-desk__err">Above the ' + money((A.cap || 0) * 100) + ' online limit. <button type="button" class="bdp-link" data-bdp-desk="quote">Request a quote</button></p>' : '') +
        (sm.priced && sm.hwPending ? '<p class="bdp-desk__err">Choose Yes or No for the hardware on ' + plural(sm.hwPending, 'part') + ' before adding to basket.</p>' : '') +
        '<button type="button" class="bdp-btn bdp-btn--primary bdp-btn--block' + (sm.priced ? '' : ' is-off') + '" data-bdp-desk="buy" aria-disabled="' + (!sm.priced) + '">' + (sm.priced && sm.hwPending ? 'Next: choose hardware' : 'Add to basket') + '</button>' +
        '<p class="bdp-desk__note">Free UK mainland delivery' + (cfg.lead ? ' · ' + esc(cfg.lead) : '') + '</p>';
    }
    if (deskCard) {
      deskCard.addEventListener('click', e => {
        const b = e.target.closest('[data-bdp-desk]'); if (!b) return;
        if (b.getAttribute('data-bdp-desk') === 'quote') A.openQuote(b);
        else if (A.summary().priced) A.addToCart();
      });
      ['input', 'change', 'click'].forEach(ev => document.addEventListener(ev, () => setTimeout(renderDesk, 60)));
      renderDesk();
    }
    // Keep the bar and review in step when the desktop list or an import changes things.
    const totalEl = document.getElementById('pb-order-total');
    if (totalEl) new MutationObserver(() => { renderDesk(); if (mounted) { renderBar(); if (S.review && !app.contains(document.activeElement)) renderReview(); } }).observe(totalEl, { childList: true, characterData: true, subtree: true });
    window.BDPartsMobile = { state: S, render, components };
  }

  function boot() {
    let n = 0;
    const t = setInterval(() => {
      if (window.BW_PARTS_API && window.BW_PARTS_API.swatches().length) { clearInterval(t); start(window.BW_PARTS_API); }
      else if (++n > 100) clearInterval(t);
    }, 100);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
